/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { Component, ContentChild, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CheckboxListModelService } from './checkbox-list-model.service';
import { CustomCheckboxContentDirective } from './custom-checkbox-content.directive';

@Component({
	selector: 'abstract-checkbox-list-element', // do not use; just here to appease Angular
	templateUrl: './abstract-checkbox-list-element.component.html', // not used; just here to appease Angular
})
export abstract class AbstractCheckboxListElement implements OnInit, OnDestroy {
	@Input() public fontSize = '16px';

	private labelSubject$ = new BehaviorSubject<string>('');
	public label$ = this.labelSubject$.asObservable();

	private iconSubject$ = new BehaviorSubject<string>('');
	public icon$ = this.iconSubject$.asObservable();

	private iconColorSubject$ = new BehaviorSubject<string>('');
	public iconColor$ = this.iconColorSubject$.asObservable();

	private readonly defaultIconSize = '28px';
	private iconSizeSubject$ = new BehaviorSubject<string>(this.defaultIconSize);
	public iconSize$ = this.iconColorSubject$.asObservable();

	public overallLayoutCssClass$: Observable<string>;
	public dynamicElementStyle$: Observable<{
		color: string;
		width: string;
		height: string;
		'font-size': string;
	}>;

	public checkboxFormControl: FormControl<boolean>;

	protected shutdownSubject$: Subject<boolean> = new Subject();

	protected abstract createFormControl(): void;
	protected abstract handleCheckboxChange(newCheckboxValue: boolean): void;
	protected abstract finishComponentInitialization(): void;

	constructor(protected checkboxListModel: CheckboxListModelService) {}

	public ngOnInit(): void {
		// Inform the model-service about this item-element and its icon:
		this.checkboxListModel.setElementHasAnIcon(
			this,
			this.iconSubject$.value && this.iconSubject$.value.trim() !== ''
		);

		// Create the FormControl for this checkbox item:
		this.createFormControl();

		// Handle checkbox value changes:
		this.checkboxFormControl.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe((newBoolValue) => {
			this.handleCheckboxChange(newBoolValue);
		});

		this.initLayoutClassObservable();
		this.initDynamicElementStyleObservable();

		this.handleEnableAndDisable();

		this.finishComponentInitialization();
	}

	protected handleEnableAndDisable() {
		// Enable/disable this item based on the enablement state of the list as a whole:
		this.checkboxListModel.disabled$.pipe(takeUntil(this.shutdownSubject$)).subscribe((isDisabled) => {
			// The "emitEvent: false" here is necessary because without it, we get VALUE change events
			// which wreak unnecessary havoc when the checkbox-set subclass thinks its checkbox's value changes,
			// when in fact it's just an enablement change
			if (isDisabled) {
				this.checkboxFormControl.disable({
					emitEvent: false,
				});
			} else {
				this.checkboxFormControl.enable({
					emitEvent: false,
				});
			}
		});
	}

	public checkboxSide$() {
		return this.checkboxListModel.checkboxSide$;
	}

	/**
	 * Possibilities for this [ngClass] are:
	 *
	 *     left-checkbox-layout-with-icons
	 *     right-checkbox-layout-with-icons
	 *     left-checkbox-layout-without-icons
	 *     right-checkbox-layout-without-icons
	 * 	   left-checkbox-layout-custom
	 *	   right-checkbox-layout-custom
	 */
	private initLayoutClassObservable() {
		this.overallLayoutCssClass$ = combineLatest([this.checkboxSide$(), this.listsIconCount$]).pipe(
			map(([side, count]) => {
				let className = side + '-checkbox-layout-';
				if (this.content?.templateRef) {
					return className + 'custom';
				}
				return className + (count !== 0 ? 'with-icons' : 'without-icons');
			}),
			takeUntil(this.shutdownSubject$)
		);
	}

	/**
	 *
	 */
	private initDynamicElementStyleObservable() {
		this.dynamicElementStyle$ = combineLatest([this.iconColor$, this.iconSizeSubject$]).pipe(
			map(([iconColor, iconSize]) => {
				return { color: iconColor, width: iconSize, height: iconSize, 'font-size': iconSize };
			}),
			takeUntil(this.shutdownSubject$)
		);
	}

	public handleClick() {
		this.checkboxFormControl.setValue(!this.checkboxFormControl.value);
	}

	@Input() public set label(newLabel: string) {
		this.labelSubject$.next(newLabel);
	}

	@Input() public set icon(newIcon: string) {
		const normalizedIcon = this.normalize(newIcon);
		this.iconSubject$.next(normalizedIcon);
		this.checkboxListModel.setElementHasAnIcon(this, !!normalizedIcon);
	}

	@Input() public set iconColor(newIconColor: string) {
		this.iconColorSubject$.next(this.normalize(newIconColor));
	}

	@Input() public set iconSize(newIconSize: string) {
		this.iconSizeSubject$.next(this.normalize(newIconSize, this.defaultIconSize));
	}

	@ContentChild(CustomCheckboxContentDirective) content: CustomCheckboxContentDirective;

	public get listsIconCount$() {
		return this.checkboxListModel.iconCount$;
	}

	private normalize(s: string, valueIfNullishOrEmpty: string | null = null) {
		// Treat empty string like null:
		return s && s.trim() === '' ? valueIfNullishOrEmpty : s;
	}

	public ngOnDestroy(): void {
		this.checkboxListModel.setElementHasAnIcon(this, false);

		this.shutdownSubject$.next(true);
		this.shutdownSubject$.complete();
	}
}
